import {
    Directive,
    Input, 
    HostListener,
    ElementRef,
    OnInit
} from '@angular/core';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import { DecimalPipe } from '@angular/common';

// Define, export a directive used to format a date 'matInput' control.
//
// Note:  much of the code in the following directive was taken from the following article:
//        https://medium.com/angular-in-depth/angular-material-matinput-control-with-thousands-separation-ebcbb7b027f4.
@Directive({
    selector: '[matInputDecimalFormat]',
    providers: [
        { provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: DecimalFormatDirective }
    ],
    standalone: false
})
export class DecimalFormatDirective implements OnInit {
    // Properties.
    @Input() digitsAfterDecimal: number;
    @Input() showTrailingZeroes: boolean;

    private _value: string | null;
    private fnOnChange = (_ignored: any) => { }; // Initial, NOOP function.
    private errorMessage: string;

    // Constructor.
    public constructor(private elementRef: ElementRef<HTMLInputElement>) {
    }

    // format decimal values on the form load as well as the blur
    public ngOnInit(): void {
        this.formatValue(this.elementRef.nativeElement.value);
    }

    public get value(): string | null {
        return this._value;
    }

    public get ErrorMessage(): string {
        return this.errorMessage;
    }

    @Input('value')
    public set value(value: string | null) {
        this._value = value;
        this.formatValue(value);
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value) {
        this._value = value.replace(/[^-^\d.]/g, '');
        this._onChange(this._value); // here to notify Angular Validators
    }

    @HostListener('blur')
    _onBlur() {
        if (this.elementRef && this.elementRef.nativeElement && this.elementRef.nativeElement.value) {
            this.formatValue(this.elementRef.nativeElement.value);
        }
        return;
    }

    @HostListener('focus')
    onFocus() {
        this.unFormatValue(); // remove commas for editing purpose
        return;
    }

    writeValue(value: any) {
        this._value = value;
        this.formatValue(this._value);
    }

    _onChange(value: any): void {
    }

    registerOnChange(fn: (value: any) => void) {
        this._onChange = fn;
    }

    // Private helper methods.
    private formatValue(value: string | null): void {
        if (value !== null) {
            // format the private value the directive wraps...
            this._value = this.applyDecimalFormatting(value);
            // ...and then set that as the value of the <input>
            this.elementRef.nativeElement.value = this._value;
        } else {
            this.elementRef.nativeElement.value = '';
        }

        return;
    }

    public applyDecimalFormatting(value: string | null): string {
        this.errorMessage = null;
        if (!value) return '';
        value = value.toString().replace(/[^0-9.-]/g, ''); // match all characters that are not a 0 thru 9, a decimal or a minus sign and remove them

        if (value == '.') value = '0';
        if (value == '-') value = '0';
        if (value.split('.').length > 2) return '';
        // PJH - 11/28/2025 - VNEXT-2073
        if (this.digitsAfterDecimal == 0 && value.indexOf('.') > -1) {
            this.errorMessage = "This field does not allow decimals";
            value = value.split('.')[0];
        }

        let decimalPipe: DecimalPipe = new DecimalPipe('en-US');
        let minDigits = 0;
        let maxDigits = 0;
        if (this.digitsAfterDecimal) {
            maxDigits = this.digitsAfterDecimal;
        }
        if (this.showTrailingZeroes) {
            minDigits = maxDigits;
        }
        let fmt = '1.' + minDigits + '-' + maxDigits;

        //console.log('applyDecimalFormatting() 2: applying ' + fmt + ' to ' + value);
        const val = decimalPipe.transform(value, fmt);
        //console.log('applyDecimalFormatting() 3: after decimalPipe.transform() ' + val);

        return val;
    }

    private unFormatValue() {
        const value = this.elementRef.nativeElement.value;
        this._value = value.replace(/[^\d.-]/g, '');
        if (value) {
            this.elementRef.nativeElement.value = this._value;
        } else {
            this.elementRef.nativeElement.value = '';
        }
    }
}
